import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useThemeStore } from './store/themeStore';
import ErrorBoundary from './components/ErrorBoundary';
import AuthInitializer from './components/AuthInitializer';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import MainLayout from './components/Layout/MainLayout';
import { ToastProvider } from './contexts/ToastContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Drivers from './pages/Drivers';
import DriverDetail from './pages/DriverDetail';
import Vehicles from './pages/Vehicles';
import VehicleDetail from './pages/VehicleDetail';
import LiveMap from './pages/LiveMap';
import Rides from './pages/Rides';
import Analytics from './pages/Analytics';
import PeakHours from './pages/PeakHours';
import Wallet from './pages/Wallet';
import RideVerifications from './pages/RideVerifications';
import RideVerificationDetail from './pages/RideVerificationDetail';
import Settings from './pages/Settings';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const { theme } = useThemeStore();

  useEffect(() => {
    // Initialize theme on app load
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthInitializer>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <PublicRoute>
                      <Signup />
                    </PublicRoute>
                  }
                />
                <Route element={<ProtectedRoute />}>
                  <Route element={<MainLayout />}>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="drivers" element={<Drivers />} />
                    <Route path="drivers/:id" element={<DriverDetail />} />
                    <Route path="vehicles" element={<Vehicles />} />
                    <Route path="vehicles/:id" element={<VehicleDetail />} />
                    <Route path="live-map" element={<LiveMap />} />
                    <Route path="rides" element={<Rides />} />
                    <Route path="ride-verifications" element={<RideVerifications />} />
                    <Route path="ride-verifications/:id" element={<RideVerificationDetail />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="peak-hours" element={<PeakHours />} />
                    <Route path="wallet" element={<Wallet />} />
                    <Route path="settings" element={<Settings />} />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </QueryClientProvider>
        </AuthInitializer>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
